import React from 'react';
import './PricingRedesign.css';

const plans = [
  {
    name: 'Free',
    subtitle: 'Best for testing ideas and launching your first site',
    price: '$0',
    priceSuffix: '/mo',
    featured: false,
    cta: 'Start free',
    ctaHref: '/signup',
    items: ['AI site generator + click-to-edit', '1 published site', '1GB storage', 'EditPage subdomain'],
  },
  {
    name: 'Growth',
    subtitle: 'The most competitive plan for service businesses and creators',
    price: '$19',
    priceSuffix: '/mo',
    featured: true,
    badge: 'Most popular',
    cta: 'Start growing',
    ctaHref: '/signup',
    socialProof: 'Chosen by 68% of new paying customers',
    annualNote: 'Save 20% with annual billing',
    items: [
      'Everything in Free, plus custom domain',
      'Unlimited pages and traffic',
      'Basic ecommerce and bookings',
      'Email capture and marketing tools',
      'No EditPage badge',
    ],
  },
  {
    name: 'Scale',
    subtitle: 'Built for teams running serious revenue',
    price: '$49',
    priceSuffix: '/mo',
    featured: false,
    cta: 'Scale now',
    ctaHref: '/signup',
    annualNote: 'Save 20% with annual billing',
    items: [
      'Everything in Growth',
      '0% platform transaction fee',
      'Priority support + onboarding',
      'Advanced analytics and automations',
      'Team collaboration access',
    ],
  },
  {
    name: 'Enterprise',
    subtitle: 'For larger brands with advanced needs',
    price: 'Custom',
    priceSuffix: '',
    featured: false,
    cta: 'Contact sales',
    ctaHref: '/contact',
    items: [
      'Everything in Scale',
      'Dedicated success manager',
      'Custom integrations and SSO',
      'Enhanced security controls',
      'Volume-based pricing',
    ],
  },
];

const PricingContent = ({ history }) => {
  const navigateTo = React.useCallback((href) => {
    if (!href) {
      return;
    }

    if (history && typeof history.push === 'function') {
      history.push(href);
      return;
    }

    window.location.assign(href);
  }, [history]);

  const growthCta = React.useMemo(() => {
    const variants = {
      a: 'Start growing',
      b: 'Get growth plan',
    };

    try {
      const storageKey = 'editpage:pricing-growth-cta-variant';
      const existing = window.localStorage.getItem(storageKey);

      if (existing && variants[existing]) {
        return variants[existing];
      }

      const next = Math.random() < 0.5 ? 'a' : 'b';
      window.localStorage.setItem(storageKey, next);
      return variants[next];
    } catch (error) {
      return variants.a;
    }
  }, []);

  return (
    <div className="pricing-redesign">
      <section className="pricing-hero">
        <div className="pricing-wrap">
          <p className="pricing-eyebrow">Plans</p>
          <h1>Fair pricing for ambitious builders</h1>
          <p>
            Start free, then choose the tier that matches your stage.
            Each upgrade adds clear value with predictable pricing.
          </p>
          <p className="pricing-annual-callout">Save 20% on annual billing for Growth and Scale.</p>
          <button
            type="button"
            onClick={() => {
              navigateTo('/signup');
            }}
          >
            Start for free
          </button>
        </div>
      </section>

      <section className="pricing-plans">
        <div className="pricing-wrap">
          <div className="pricing-grid">
            {plans.map(plan => (
              <article
                className={`pricing-card ${plan.featured ? 'featured' : ''}`}
                key={plan.name}
              >
                {plan.badge ? <span className="pricing-badge">{plan.badge}</span> : null}
                <h2>{plan.name}</h2>
                <p className="pricing-subtitle">{plan.subtitle}</p>
                <p className="pricing-amount">
                  {plan.price}
                  <span>{plan.priceSuffix}</span>
                </p>
                {plan.socialProof ? <p className="pricing-social-proof">{plan.socialProof}</p> : null}
                {plan.annualNote ? <p className="pricing-annual-note">{plan.annualNote}</p> : null}
                <ul>
                  {plan.items.map(item => (
                    <li key={item}>{item}</li>
                  ))}
                </ul>
                <button
                  type="button"
                  className={plan.featured ? 'solid' : 'outline'}
                  onClick={() => {
                    navigateTo(plan.ctaHref);
                  }}
                >
                  {plan.name === 'Growth' ? growthCta : plan.cta}
                </button>
              </article>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default PricingContent;
